import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';
import Webcam from 'react-webcam';

// --- IMPORT DE MATHLIVE ---
import 'mathlive';

import Loading from './Loading';
import SmartEx from './smartex';
import { API_URLS } from '../config/api';
import { subscribeUserToPush } from '../utils/pushNotifications';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

/* ============================================================
   CONSTANTES (même langage visuel que SmartExChap)
   Police : Outfit. Seules transform / opacity / height sont animées.
   ============================================================ */
const EASE = [0.22, 1, 0.36, 1];

const FONT_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;700;800;900&display=swap');
.spx { font-family: 'Outfit', system-ui, -apple-system, 'Segoe UI', sans-serif; }
.spx button { -webkit-tap-highlight-color: transparent; font-family: inherit; }
.spx math-field::part(virtual-keyboard-toggle) { display: none !important; }
.spx math-field::part(menu-toggle) { display: none !important; }
`;

/* Boutons "3D" : le relief donne l'envie d'appuyer */
const BTN_BLUE = 'bg-[#4A90E2] text-white border-b-4 border-[#2F6FB8] active:border-b-0 active:translate-y-1';
const BTN_NAVY = 'bg-[#2B4360] text-white border-b-4 border-[#1A2B40] active:border-b-0 active:translate-y-1';
const BTN_GREEN = 'bg-[#7C9374] text-white border-b-4 border-[#5D7355] active:border-b-0 active:translate-y-1';
const BTN_LIGHT = 'bg-white text-[#2B4360] border-2 border-b-4 border-slate-200 active:border-b-2 active:translate-y-[2px]';

const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A90E2] focus-visible:ring-offset-2';

const LOGO_URL = 'https://res.cloudinary.com/do5zemcrk/image/upload/v1766679468/Gemini_Generated_Image_35upzx35upzx35up_1_farsag.png';

/* ============================================================
   COMPOSANTS UI
   ============================================================ */

// Déplie / replie son contenu en douceur, sur place
const Collapse = ({ open, children }) => (
  <AnimatePresence initial={false}>
    {open && (
      <motion.div
        key="content"
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.32, ease: EASE }}
        style={{ overflow: 'hidden' }}
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
);

// Chevron dessiné en CSS (aucune icône)
const Chevron = ({ open }) => (
  <span
    aria-hidden="true"
    className={`block w-2.5 h-2.5 border-r-[3px] border-b-[3px] border-current rounded-[1px] transition-transform duration-300 ${open ? '-rotate-[135deg] translate-y-[3px]' : 'rotate-45 -translate-y-[2px]'}`}
  />
);

const Spinner = ({ className = '' }) => (
  <span className={`block rounded-full border-[3px] border-slate-200 border-t-[#2B4360] animate-spin ${className}`} />
);

const StateCard = ({ title, text, error }) => (
  <div className="rounded-3xl border-2 border-slate-200 bg-white p-8 text-center" role={error ? 'alert' : undefined}>
    <h3 className={`text-xl font-black ${error ? 'text-red-600' : 'text-[#1A2B40]'}`}>{title}</h3>
    <p className="text-sm text-slate-500 mt-2 leading-relaxed">{text}</p>
  </div>
);

const HomeworkSkeleton = () => (
  <li className="rounded-3xl border-2 border-slate-200 bg-white p-4 flex items-center gap-4 animate-pulse">
    <div className="w-12 h-12 rounded-2xl bg-slate-100 shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-4 w-3/4 rounded-full bg-slate-100" />
      <div className="h-3 w-1/4 rounded-full bg-slate-100" />
    </div>
  </li>
);

// Sélecteur À faire / Terminés : deux choix visibles en permanence
const TabSwitch = ({ activeTab, onChange, todoCount, doneCount }) => {
  const tabs = [
    { id: 'todo', label: 'À faire', count: todoCount },
    { id: 'done', label: 'Terminés', count: doneCount }
  ];
  return (
    <div role="tablist" aria-label="Filtrer les devoirs" className="relative mt-6 grid grid-cols-2 p-1 rounded-2xl bg-slate-200/70 max-w-md">
      <motion.span
        aria-hidden="true"
        className="absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] rounded-xl bg-white shadow-sm"
        animate={{ x: activeTab === 'done' ? '100%' : '0%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 400 }}
      />
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={activeTab === t.id}
          onClick={() => activeTab !== t.id && onChange(t.id)}
          className={`relative z-10 min-h-[44px] rounded-xl text-sm font-extrabold transition-colors duration-200 touch-manipulation ${FOCUS} ${
            activeTab === t.id ? 'text-[#1A2B40]' : 'text-slate-500'
          }`}
        >
          {t.label}
          <span
            className={`ml-2 inline-block min-w-[22px] px-1.5 py-0.5 rounded-full text-xs font-black tabular-nums transition-colors duration-200 ${
              activeTab === t.id ? 'bg-[#4A90E2]/15 text-[#2F6FB8]' : 'bg-slate-300/50 text-slate-500'
            }`}
          >
            {t.count}
          </span>
        </button>
      ))}
    </div>
  );
};

const HomeworkCard = ({ hw, index, isDone, isOpen, deadlineInfo, score, onToggle, onCamera }) => {
  let scoreStyle = 'bg-slate-100 text-slate-600';
  if (isDone) {
    if (score >= 80) scoreStyle = 'bg-[#7C9374] text-white';
    else if (score >= 50) scoreStyle = 'bg-amber-100 text-amber-800';
    else scoreStyle = 'bg-red-100 text-red-700';
  }

  return (
    <motion.li
      layout="position"
      transition={{ duration: 0.32, ease: EASE }}
      className={`rounded-3xl border-2 transition-colors duration-200 ${
        isDone ? 'border-slate-200 bg-white' : isOpen ? 'border-[#4A90E2] bg-white' : 'border-slate-200 bg-white'
      }`}
    >
      <button
        type="button"
        onClick={() => !isDone && onToggle(hw.id)}
        disabled={isDone}
        aria-expanded={isDone ? undefined : isOpen}
        className={`w-full flex items-center gap-4 p-4 text-left rounded-3xl touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A90E2] ${isDone ? 'cursor-default' : ''}`}
      >
        <span
          className={`w-12 h-12 shrink-0 rounded-2xl flex items-center justify-center text-xl font-black tabular-nums transition-colors duration-200 ${
            isDone ? 'bg-[#7C9374]/15 text-[#5D7355]' : isOpen ? 'bg-[#4A90E2] text-white' : 'bg-[#2B4360]/[0.07] text-[#2B4360]'
          }`}
        >
          {index + 1}
        </span>

        <span className="flex-1 min-w-0">
          <span className="block text-xs font-bold text-slate-500 truncate">{hw.chapter}</span>
          <span className="block text-[17px] font-extrabold text-[#1A2B40] leading-tight line-clamp-2 mt-0.5">
            {hw.exercise_info || 'Exercice'}
          </span>
          {!isDone && (
            <span
              className={`inline-block mt-2 px-2.5 py-1 rounded-full text-xs font-extrabold ${
                deadlineInfo?.isPast ? 'bg-red-50 text-red-600' : 'bg-[#2B4360]/[0.07] text-[#2B4360]'
              }`}
            >
              {deadlineInfo?.isPast ? 'En retard : ' : 'Pour : '}
              {deadlineInfo?.text || 'sans date'}
            </span>
          )}
        </span>

        {isDone ? (
          <span className={`shrink-0 min-w-[56px] px-3 py-2 rounded-2xl text-center ${scoreStyle}`} aria-label={`Note ${score} pour cent`}>
            <span className="block text-lg font-black tabular-nums leading-none">{score}%</span>
          </span>
        ) : (
          <span className={`shrink-0 w-9 h-9 flex items-center justify-center ${isOpen ? 'text-[#4A90E2]' : 'text-slate-400'}`}>
            <Chevron open={isOpen} />
          </span>
        )}
      </button>

      {!isDone && (
        <Collapse open={isOpen}>
          <div className="px-4 pb-4">
            <button
              type="button"
              onClick={(e) => onCamera(e, hw.id)}
              className={`${BTN_BLUE} w-full min-h-[52px] rounded-2xl font-extrabold text-base transition-transform touch-manipulation ${FOCUS}`}
            >
              Photographier mon devoir
            </button>
          </div>
        </Collapse>
      )}
    </motion.li>
  );
};

/* ============================================================
   MATHLIVE : 1 CHAMP = 1 LIGNE PHYSIQUE DE LA PHOTO
   ============================================================ */
const LigneMathLive = ({ index, valeurInitiale, onChange }) => {
  const mfRef = useRef(null);

  useEffect(() => {
    const mathField = mfRef.current;
    if (mathField) {
      // Bouclier anti-mots collés : si \text{} est absent, on protège les espaces
      let safeValue = valeurInitiale || "";
      if (!safeValue.includes('\\text')) {
        safeValue = safeValue.replace(/ /g, '\\ ');
      }

      mathField.value = safeValue;

      const handleInput = (e) => onChange(index, e.target.value);
      mathField.addEventListener('input', handleInput);
      return () => mathField.removeEventListener('input', handleInput);
    }
  }, []); // Le tableau vide empêche l'éditeur de sauter pendant la frappe

  return (
    <div className="w-full bg-[#F4F7FB] border-2 border-slate-200 rounded-2xl p-2.5 pl-4 focus-within:border-[#4A90E2] focus-within:bg-white transition-colors duration-200 flex items-center gap-3">
      <span className="w-5 text-sm font-black text-[#4A90E2] tabular-nums shrink-0 select-none">{index + 1}</span>

      {/* Mobile : scroll horizontal propre */}
      <div className="flex-1 min-w-0 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <math-field
          ref={mfRef}
          style={{
            fontSize: '1.15rem',
            minWidth: '100%',
            minHeight: '35px',
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#1A2B40'
          }}
        />
      </div>
    </div>
  );
};

const EditeurMathVisuel = ({ qKey, lignesInitiales, onSave }) => {
  const [lignes, setLignes] = useState(lignesInitiales);

  const handleLigneChange = (index, nouvelleValeur) => {
    const nouvellesLignes = [...lignes];
    nouvellesLignes[index] = nouvelleValeur;
    setLignes(nouvellesLignes);
    onSave(qKey, nouvellesLignes);
  };

  return (
    <li className="rounded-3xl border-2 border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-lg font-black text-[#1A2B40]">{qKey}</h2>
        <span className="text-xs font-medium text-slate-500 text-right">Touche une ligne pour la corriger</span>
      </div>

      <div className="flex flex-col gap-2">
        {lignes.length > 0 ? (
          lignes.map((ligne, i) => (
            <LigneMathLive key={i} index={i} valeurInitiale={ligne} onChange={handleLigneChange} />
          ))
        ) : (
          <p className="py-4 text-center text-sm font-medium text-slate-500">Rien n'a été lu sur cette question.</p>
        )}
      </div>
    </li>
  );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
const MesDevoirsInner = ({ onLogout }) => {
  const [todoHomeworks, setTodoHomeworks] = useState([]);
  const [doneHomeworks, setDoneHomeworks] = useState([]);
  const [activeTab, setActiveTab] = useState('todo');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classInfo, setClassInfo] = useState({ name: 'Chargement...', teacherName: '...' });
  const navigate = useNavigate();

  const [currentView, setCurrentView] = useState('list');
  const [ocrData, setOcrData] = useState(null);
  const [currentImageFile, setCurrentImageFile] = useState(null);

  const [expandedCardId, setExpandedCardId] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [activeHomeworkId, setActiveHomeworkId] = useState(null);
  const fileInputRef = useRef(null);

  const [userInfo] = useState(() => JSON.parse(sessionStorage.getItem('user_info') || '{}'));
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);

  // --- ÉTATS CAMÉRA ---
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const [capturedImage, setCapturedImage] = useState(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const [showValidationPrompt, setShowValidationPrompt] = useState(false);
  const webcamRef = useRef(null);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      const timer = setTimeout(() => setShowNotificationBanner(true), 2500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleEnableNotifications = async () => {
    const success = await subscribeUserToPush();
    if (success) setShowNotificationBanner(false);
    else alert("Impossible d'activer les alertes. Vérifiez les réglages de votre appareil.");
  };

  useEffect(() => {
    if (!userInfo || !Object.keys(userInfo).length) {
      setError("Session expirée. Veuillez vous reconnecter.");
      setLoading(false);
      return;
    }

    const fetchHomeworksAndClass = async () => {
      try {
        let currentClassroomId = userInfo.classroom_id;
        let currentClassName = 'Classe non assignée';
        let currentTeacherId = null;
        let currentTeacherName = 'Professeur';

        if (!currentClassroomId && userInfo.firstName && userInfo.lastName) {
          const fullName = `${userInfo.firstName} ${userInfo.lastName}`;
          const { data: classData } = await supabase.from('classrooms').select('id, name, teacher_id').contains('invited_students', JSON.stringify([fullName])).maybeSingle();
          if (classData) {
            currentClassroomId = classData.id;
            currentClassName = classData.name;
            currentTeacherId = classData.teacher_id;
            await supabase.from('users').update({ classroom_id: classData.id }).eq('id', userInfo.id);
            sessionStorage.setItem('user_info', JSON.stringify({ ...userInfo, classroom_id: classData.id }));
          }
        } else if (currentClassroomId) {
          const { data: classData } = await supabase.from('classrooms').select('name, teacher_id').eq('id', currentClassroomId).maybeSingle();
          if (classData) {
            currentClassName = classData.name;
            currentTeacherId = classData.teacher_id;
          }
        }

        if (currentTeacherId) {
          const { data: teacherData } = await supabase.from('users').select('first_name, last_name').eq('id', currentTeacherId).maybeSingle();
          if (teacherData) currentTeacherName = `${teacherData.first_name} ${teacherData.last_name}`;
        }

        if (!currentClassroomId) {
          setError("Accès restreint. Vous n'êtes rattaché à aucune classe active.");
          setLoading(false);
          return;
        }

        setClassInfo({ name: currentClassName, teacherName: currentTeacherName });

        const { data: hwData, error: hwError } = await supabase.from('homeworks').select('*').eq('classroom_id', currentClassroomId).order('created_at', { ascending: false });
        if (hwError) throw hwError;

        const { data: subData, error: subError } = await supabase.from('homework_submissions').select('*').eq('student_id', userInfo.id).eq('status', 'COMPLETED');
        if (subError) throw subError;

        const submissionsMap = {};
        if (subData) subData.forEach(sub => submissionsMap[sub.homework_id] = sub);

        const todo = [];
        const done = [];

        (hwData || []).forEach(hw => {
          if (submissionsMap[hw.id]) done.push({ ...hw, submission: submissionsMap[hw.id] });
          else todo.push(hw);
        });

        setTodoHomeworks(todo);
        setDoneHomeworks(done);

      } catch (err) {
        setError("Une erreur de synchronisation est survenue.");
      } finally {
        setLoading(false);
      }
    };

    fetchHomeworksAndClass();
  }, [userInfo]);

  const handleLogout = () => {
    if (onLogout) onLogout();
    else {
      sessionStorage.removeItem('user_info');
      navigate('/auth');
    }
  };

  const formatDeadline = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const today = new Date();
    const isPast = date < today;

    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let text = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    if (diffDays === 0) text = "Aujourd'hui";
    else if (diffDays === 1) text = "Demain";

    return { text, isPast };
  };

  const handleCardClick = (id) => {
    setExpandedCardId(expandedCardId === id ? null : id);
  };

  const processImageForOCR = (file) => {
    setIsCameraOpen(false);
    setCapturedImage(null);
    setCurrentView('loading_ocr');
    setCurrentImageFile(file);

    const formData = new FormData();
    formData.append('file', file);

    const baseUrl = API_URLS.ANALYZE_PROGRESSIVE.replace('/analyze-progressive', '');
    const extractUrl = `${baseUrl}/extract-ocr`;

    fetch(extractUrl, { method: 'POST', body: formData })
    .then(res => {
      if (!res.ok) throw new Error("Erreur serveur lors de la lecture");
      return res.json();
    })
    .then(data => {
      const parsedData = {};
      Object.keys(data.extracted_data).forEach(qKey => {
        const qData = data.extracted_data[qKey];
        if (typeof qData === 'object' && !Array.isArray(qData)) {
          parsedData[qKey] = Object.values(qData).map(v => v.toString().trim());
        } else if (Array.isArray(qData)) {
          parsedData[qKey] = qData;
        } else {
          parsedData[qKey] = [qData.toString().trim()];
        }
      });
      setOcrData(parsedData);
      setCurrentView('validating_ocr');
    })
    .catch(err => {
      console.error("Erreur OCR:", err);
      setCurrentView('list');
      alert("Oops ! L'image est floue ou illisible. Essaie de reprendre la photo bien à plat.");
    });
  };

  const submitValidatedOCR = () => {
    setCurrentView('loading_analysis');
    setAnalysisData(null);

    const backendData = {};
    Object.keys(ocrData).forEach(qKey => {
      const lignesArray = ocrData[qKey];
      backendData[qKey] = {};
      lignesArray.forEach((ligneTexte, index) => {
        backendData[qKey][`L${index + 1}`] = ligneTexte;
      });
    });

    const formData = new FormData();
    formData.append('file', currentImageFile, 'devoir.jpg');
    formData.append('user_id', userInfo.id);
    formData.append('exercise_id', activeHomeworkId);
    formData.append('exam_mode', 'false');
    formData.append('validated_solutions', JSON.stringify(backendData));

    fetch(API_URLS.ANALYZE_PROGRESSIVE, { method: 'POST', body: formData })
    .then(res => res.json())
    .then(data => {
      setAnalysisData({ data, studentName: userInfo.firstName });
      setCurrentView('results');
    })
    .catch(err => {
      console.error("Erreur d'analyse:", err);
      setCurrentView('list');
      alert("Un problème de connexion est survenu. Réessaie !");
    });
  };

  const handleOcrItemSave = useCallback((qKey, newLignesArray) => {
    setOcrData(prev => ({ ...prev, [qKey]: newLignesArray }));
  }, []);

  const openCamera = (e, hwId) => {
    e.stopPropagation();
    setActiveHomeworkId(hwId);
    setCapturedImage(null);
    setShowValidationPrompt(false);
    setIsCameraOpen(true);
  };

  // --- GESTION CAMÉRA & UX PHOTO ---
  const capturePhoto = useCallback(() => {
    // 1. Effet de flash
    setIsFlashing(true);
    setTimeout(() => setIsFlashing(false), 150);

    // 2. Capture de l'image
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      setCapturedImage(imageSrc);

      // 3. Compteur intelligent (1 fois sur 3)
      let count = parseInt(localStorage.getItem('spyrale_photo_count') || '0', 10);
      count += 1;
      localStorage.setItem('spyrale_photo_count', count.toString());

      if (count % 3 === 0) {
        setShowValidationPrompt(true);
      } else {
        setTimeout(() => {
          submitCapturedPhoto(imageSrc);
        }, 1200);
      }
    }
  }, [webcamRef]);

  const submitCapturedPhoto = async (base64Str) => {
    try {
      const res = await fetch(base64Str);
      const blob = await res.blob();
      processImageForOCR(blob);
    } catch (e) {
      console.error("Erreur transformation image", e);
    }
  };

  const closeCamera = () => {
    setIsCameraOpen(false);
    setCapturedImage(null);
    setShowValidationPrompt(false);
  };

  const toggleCamera = () => setFacingMode(prev => prev === "environment" ? "user" : "environment");
  const handleGalleryChoice = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || !activeHomeworkId) return;
    processImageForOCR(file);
    e.target.value = null;
  };

  const handleBackToList = () => window.location.reload();

  // --- RENDUS CONDITIONNELS DES VUES ---

  if (currentView === 'loading_ocr') {
    return <Loading mode="ocr" analysisData={null} onStartTransition={() => {}} />;
  }

  if (currentView === 'loading_analysis') {
    return <Loading mode="analysis" analysisData={analysisData} onStartTransition={() => setCurrentView('results')} />;
  }

  if (currentView === 'results') {
    return <SmartEx data={analysisData?.data} onBack={handleBackToList} isProgressive={true} studentName={analysisData?.studentName || userInfo.firstName} homeworkId={activeHomeworkId} />;
  }

  /* ---------------- VÉRIFICATION DE LA LECTURE ---------------- */
  if (currentView === 'validating_ocr') {
    return (
      <div className="spx min-h-[100dvh] w-full bg-[#F4F7FB] text-slate-900 relative">
        <style dangerouslySetInnerHTML={{ __html: FONT_CSS }} />

        <div className="max-w-[720px] mx-auto px-5" style={{ paddingBottom: 'calc(8rem + env(safe-area-inset-bottom))' }}>
          <header className="flex items-center justify-between" style={{ paddingTop: 'max(env(safe-area-inset-top), 1rem)' }}>
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Spyrale" width="40" height="40" decoding="async" className="w-10 h-10 rounded-xl border-2 border-slate-200 bg-white object-cover" />
              <span className="text-xl font-black tracking-tight text-[#2B4360]">Spyrale</span>
            </div>
            <button
              type="button"
              onClick={() => setCurrentView('list')}
              className={`min-h-[44px] px-4 rounded-xl border-2 border-slate-200 bg-white text-sm font-bold text-slate-600 active:bg-slate-50 touch-manipulation ${FOCUS}`}
            >
              Annuler
            </button>
          </header>

          <section className="pt-10 pb-6">
            <h1 className="text-[40px] sm:text-5xl font-black text-[#1A2B40] tracking-tight leading-[1.05]">
              Vérifie ce que l'IA a lu.
            </h1>
            <p className="mt-3 text-[15px] font-medium text-slate-500 max-w-md leading-relaxed">
              Corrige chaque ligne si besoin, puis envoie ton devoir.
            </p>
          </section>

          <main>
            <ul className="space-y-3">
              {ocrData && Object.keys(ocrData).map((qKey) => (
                <EditeurMathVisuel
                  key={qKey}
                  qKey={qKey}
                  lignesInitiales={ocrData[qKey]}
                  onSave={handleOcrItemSave}
                />
              ))}
            </ul>
          </main>
        </div>

        <motion.div
          initial={{ y: '130%' }} animate={{ y: 0 }}
          transition={{ type: 'spring', damping: 30, stiffness: 320 }}
          className="fixed inset-x-0 z-40 px-4 pointer-events-none"
          style={{ bottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <div className="max-w-[720px] mx-auto bg-[#1A2B40] text-white rounded-3xl shadow-2xl p-3 pl-5 pointer-events-auto flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-extrabold leading-tight truncate">Tout est bon ?</p>
              <p className="text-xs text-white/60 truncate">Ton devoir sera analysé.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setCurrentView('list')}
                className={`min-h-[52px] px-4 rounded-2xl font-extrabold text-sm bg-white/10 text-white border-b-4 border-transparent active:translate-y-1 transition-transform touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white`}
              >
                Nouvelle photo
              </button>
              <button
                type="button"
                onClick={submitValidatedOCR}
                className={`${BTN_GREEN} min-h-[52px] px-7 rounded-2xl font-extrabold text-sm transition-transform touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white`}
              >
                Envoyer
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  /* ---------------- ÉCRAN DES DEVOIRS ---------------- */
  const currentList = activeTab === 'todo' ? todoHomeworks : doneHomeworks;
  const isDoneTab = activeTab === 'done';

  return (
    <div className="spx min-h-[100dvh] w-full bg-[#F4F7FB] text-slate-900 relative overflow-x-hidden">
      <style dangerouslySetInnerHTML={{ __html: FONT_CSS }} />

      {/* Bandeau d'activation des alertes */}
      <AnimatePresence>
        {showNotificationBanner && (
          <motion.div
            initial={{ y: '-130%', opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: '-130%', opacity: 0 }}
            transition={{ duration: 0.32, ease: EASE }}
            className="fixed inset-x-4 z-[100] max-w-md mx-auto"
            style={{ top: 'max(1rem, env(safe-area-inset-top))' }}
            role="dialog"
            aria-label="Activer les alertes"
          >
            <div className="bg-[#1A2B40] text-white p-4 rounded-3xl shadow-2xl">
              <p className="text-base font-extrabold">Active tes alertes</p>
              <p className="text-sm text-white/60 mt-1 leading-snug">
                Sois prévenu quand ton professeur te donne un devoir à faire !
              </p>
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowNotificationBanner(false)}
                  className="flex-1 min-h-[48px] rounded-2xl bg-white/10 text-white/80 text-sm font-bold active:bg-white/20 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  Plus tard
                </button>
                <button
                  type="button"
                  onClick={handleEnableNotifications}
                  className={`${BTN_BLUE} flex-1 min-h-[48px] rounded-2xl text-sm font-extrabold transition-transform touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white`}
                >
                  Activer
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Appareil photo */}
      <AnimatePresence>
        {isCameraOpen && (
          <motion.div
            initial={{ opacity: 0, y: '100%' }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: '100%' }}
            transition={{ duration: 0.32, ease: EASE }}
            className="fixed inset-0 z-[999] bg-[#1A2B40] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label="Photographier mon devoir"
          >
            {/* Flash */}
            <AnimatePresence>
              {isFlashing && (
                <motion.div initial={{ opacity: 1 }} animate={{ opacity: 0 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-white z-[1000] pointer-events-none" />
              )}
            </AnimatePresence>

            <div
              className="absolute top-0 inset-x-0 px-4 pb-4 flex justify-between items-center z-20"
              style={{ paddingTop: 'max(env(safe-area-inset-top), 1rem)' }}
            >
              <button
                type="button"
                onClick={closeCamera}
                className="min-h-[44px] px-5 rounded-xl bg-[#1A2B40]/80 text-white text-sm font-extrabold active:scale-95 transition-transform touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                Fermer
              </button>
              {!capturedImage && (
                <button
                  type="button"
                  onClick={toggleCamera}
                  className="min-h-[44px] px-5 rounded-xl bg-[#1A2B40]/80 text-white text-sm font-extrabold active:scale-95 transition-transform touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  Retourner
                </button>
              )}
            </div>

            <div className="relative flex-1 bg-black overflow-hidden flex items-center justify-center">
              {!capturedImage ? (
                <Webcam audio={false} ref={webcamRef} screenshotFormat="image/jpeg" videoConstraints={{ facingMode: facingMode }} className="absolute w-full h-full object-cover" />
              ) : (
                <motion.img initial={{ scale: 1.05 }} animate={{ scale: 1 }} transition={{ duration: 0.25, ease: EASE }} src={capturedImage} alt="Photo prise" className="absolute w-full h-full object-cover" />
              )}

              {/* Cadre de guidage */}
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-6">
                <div className="w-full max-w-sm aspect-[3/4] border-2 border-white/20 rounded-3xl relative shadow-[0_0_0_9999px_rgba(26,43,64,0.65)]">
                  {!capturedImage && (
                    <div className="absolute -top-14 inset-x-0 text-center">
                      <span className="inline-block bg-[#1A2B40]/80 text-white text-sm font-bold px-4 py-2 rounded-full">
                        Cadre ton exercice, bien à plat
                      </span>
                    </div>
                  )}
                  <div className="absolute top-0 left-0 w-10 h-10 border-t-4 border-l-4 border-[#4A90E2] rounded-tl-3xl" />
                  <div className="absolute top-0 right-0 w-10 h-10 border-t-4 border-r-4 border-[#4A90E2] rounded-tr-3xl" />
                  <div className="absolute bottom-0 left-0 w-10 h-10 border-b-4 border-l-4 border-[#4A90E2] rounded-bl-3xl" />
                  <div className="absolute bottom-0 right-0 w-10 h-10 border-b-4 border-r-4 border-[#4A90E2] rounded-br-3xl" />
                </div>
              </div>
            </div>

            {/* Contrôles du bas */}
            {!capturedImage ? (
              <div
                className="bg-[#1A2B40] grid grid-cols-3 items-center px-6 pt-5 z-20"
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}
              >
                <button
                  type="button"
                  onClick={handleGalleryChoice}
                  className="justify-self-start min-h-[48px] px-5 rounded-xl bg-white/10 text-white text-sm font-extrabold active:scale-95 transition-transform touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  Galerie
                </button>
                <button
                  type="button"
                  onClick={capturePhoto}
                  aria-label="Prendre la photo"
                  className="justify-self-center w-20 h-20 rounded-full border-4 border-white p-1 active:scale-90 transition-transform touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A90E2] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1A2B40]"
                >
                  <span className="block w-full h-full bg-white rounded-full" />
                </button>
                <span />
              </div>
            ) : showValidationPrompt ? (
              <motion.div
                initial={{ y: '100%' }} animate={{ y: 0 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="absolute bottom-0 inset-x-0 bg-white rounded-t-[32px] p-6 pt-8 z-30 shadow-2xl"
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)' }}
              >
                <h3 className="text-2xl font-black text-[#1A2B40] mb-2">Est-ce bien net ?</h3>
                <p className="text-slate-500 text-[15px] font-medium mb-6 leading-relaxed">
                  Vérifie que les calculs sont lisibles pour que ton professeur puisse corriger sans erreur.
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setCapturedImage(null); setShowValidationPrompt(false); }}
                    className={`${BTN_LIGHT} flex-1 min-h-[52px] rounded-2xl font-extrabold text-sm transition-transform touch-manipulation ${FOCUS}`}
                  >
                    Recommencer
                  </button>
                  <button
                    type="button"
                    onClick={() => submitCapturedPhoto(capturedImage)}
                    className={`${BTN_BLUE} flex-1 min-h-[52px] rounded-2xl font-extrabold text-sm transition-transform touch-manipulation ${FOCUS}`}
                  >
                    C'est net
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: EASE }}
                className="absolute inset-x-6 bg-[#1A2B40] rounded-3xl p-5 flex items-center justify-center gap-3 z-30 text-white shadow-2xl"
                style={{ bottom: 'max(env(safe-area-inset-bottom), 2rem)' }}
                role="status"
              >
                <span className="block w-6 h-6 rounded-full border-[3px] border-white/20 border-t-[#4A90E2] animate-spin" />
                <span className="font-extrabold">Lecture de la copie</span>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />

      <div className="max-w-[720px] mx-auto px-5" style={{ paddingBottom: 'calc(3rem + env(safe-area-inset-bottom))' }}>
        {/* En-tête */}
        <header className="flex items-center justify-between" style={{ paddingTop: 'max(env(safe-area-inset-top), 1rem)' }}>
          <div className="flex items-center gap-3 min-w-0">
            <img src={LOGO_URL} alt="Spyrale" width="40" height="40" decoding="async" className="w-10 h-10 rounded-xl border-2 border-slate-200 bg-white object-cover shrink-0" />
            <span className="text-xl font-black tracking-tight text-[#2B4360]">Spyrale</span>
            {classInfo.name && !loading && (
              <span className="px-2.5 py-1 rounded-full bg-[#7C9374]/15 text-[#5D7355] text-xs font-extrabold truncate">{classInfo.name}</span>
            )}
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className={`min-h-[44px] px-4 rounded-xl border-2 border-slate-200 bg-white text-sm font-bold text-slate-600 active:bg-slate-50 touch-manipulation shrink-0 ${FOCUS}`}
          >
            Déconnexion
          </button>
        </header>

        {/* Titre */}
        <section className="pt-10 pb-6">
          <p className="text-base font-bold text-slate-500">Bonjour {userInfo.firstName || 'toi'}</p>
          <h1 className="mt-1 text-[40px] sm:text-5xl font-black text-[#1A2B40] tracking-tight leading-[1.05]">
            {isDoneTab ? 'Tes devoirs terminés.' : 'Tes devoirs à rendre.'}
          </h1>
          <p className="mt-3 text-[15px] font-medium text-slate-500 max-w-md leading-relaxed">
            {isDoneTab
              ? 'Retrouve ici tes notes et ton historique.'
              : 'Touche un devoir, puis photographie ta copie pour l\'envoyer.'}
          </p>
          {!loading && !error && (
            <TabSwitch
              activeTab={activeTab}
              onChange={(tab) => { setActiveTab(tab); setExpandedCardId(null); }}
              todoCount={todoHomeworks.length}
              doneCount={doneHomeworks.length}
            />
          )}
        </section>

        {/* Liste */}
        <main>
          {loading ? (
            <ul className="space-y-3" aria-busy="true" aria-label="Chargement des devoirs">
              {[0, 1, 2, 3].map((k) => <HomeworkSkeleton key={k} />)}
            </ul>
          ) : error ? (
            <StateCard error title="Chargement impossible" text={error} />
          ) : currentList.length === 0 ? (
            <StateCard
              title={isDoneTab ? 'Aucun devoir terminé' : 'Aucun devoir à rendre'}
              text={isDoneTab ? 'Tes devoirs terminés apparaîtront ici.' : 'Tu es à jour. Profite de ton temps libre.'}
            />
          ) : (
            <ul className="space-y-3">
              {currentList.map((hw, index) => {
                const deadlineInfo = formatDeadline(hw.questions?.deadline);
                const score = hw.submission?.evaluations?.score ?? 0;
                return (
                  <HomeworkCard
                    key={hw.id}
                    hw={hw}
                    index={index}
                    isDone={isDoneTab}
                    isOpen={!isDoneTab && expandedCardId === hw.id}
                    deadlineInfo={deadlineInfo}
                    score={score}
                    onToggle={handleCardClick}
                    onCamera={openCamera}
                  />
                );
              })}
            </ul>
          )}
        </main>
      </div>
    </div>
  );
};

const MesDevoirs = (props) => (
  <MotionConfig reducedMotion="user">
    <MesDevoirsInner {...props} />
  </MotionConfig>
);

export default MesDevoirs;